'use client';
import { Tooltip as MaterialTooltip } from '@material-tailwind/react';
import classNames from 'classnames';
import React, { useState, useEffect, useRef, MouseEvent } from 'react';
import { MaterialIcon } from './MaterialIcon';

interface TooltipProps {
  children?: React.ReactNode;
  /** Replaces the "i" icon as the trigger. */
  trigger?: React.ReactNode;
  content: string | React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  onOpenEvent?: 'hover' | 'click';
  onClick?: () => void;
  onHover?: () => void;
  infoClassName?: string;
  rootRef?: React.Ref<HTMLSpanElement>;
  infoStyle?: React.CSSProperties;
  /** Merged over the default tooltip card classes. */
  tooltipClassName?: string;
}

export const InlineTooltip: React.FC<TooltipProps> = ({
  children,
  trigger,
  content,
  placement = 'bottom',
  onOpenEvent = 'hover',
  infoClassName,
  infoStyle,
  onClick = () => {},
  onHover = () => {},
  rootRef,
  tooltipClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isClickable = onOpenEvent === 'click';
  const withHover = onOpenEvent === 'hover';
  // A custom trigger carries data (not a decorative "i"), so it is focusable
  // and opens from the keyboard. So is any trigger in click mode, where
  // activating it is the only way to read the content (CMS-1905's table
  // marker) — a mouse-only trigger hides that content from keyboard users.
  const isFocusable = trigger !== undefined || isClickable;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (!isOpen) onClick();
    setIsOpen(!isOpen);
  };

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isClickable) {
      if (!isOpen) {
        onClick();
      }
      setIsOpen(!isOpen);
      return;
    }
    onClick();
  };

  const handleClickOutside: EventListener = (evt: Event) => {
    if (
      tooltipRef.current &&
      !tooltipRef.current.contains(evt.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isClickable) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      if (isClickable) {
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [isClickable]);

  const tooltipContent =
    typeof content === 'string' ? (
      <div
        className="alpha-badge"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: content }}
      />
    ) : (
      content
    );

  return (
    <MaterialTooltip
      ref={tooltipRef}
      interactive
      open={isOpen}
      content={tooltipContent}
      placement={placement}
      className={classNames(
        'absolute left-0 top-0 z-30 min-w-50 max-w-50 cursor-default rounded-lg p-3 text-left shadow bg-white text-black text-sm md:min-w-50 md:max-w-75',
        tooltipClassName,
      )}
    >
      {/*eslint-disable-next-line jsx-a11y/no-static-element-interactions*/}
      <span
        ref={rootRef}
        onClick={handleClick}
        onMouseEnter={
          withHover
            ? () => {
                if (!isOpen) {
                  onHover();
                }
                setIsOpen(true);
              }
            : undefined
        }
        onMouseLeave={withHover ? () => setIsOpen(false) : undefined}
        {...(isFocusable
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-expanded': isOpen,
              onKeyDown: handleKeyDown,
              onBlur: () => setIsOpen(false),
            }
          : {})}
        className={infoClassName}
        style={infoStyle}
      >
        {trigger ?? (
          <MaterialIcon icon="info" className="inline align-bottom" px={18} />
        )}
        {/* Part of the trigger, not beside it: a label outside this span would
            look clickable and do nothing. Callers that pass none keep the
            icon-only trigger they had. */}
        {children}
      </span>
    </MaterialTooltip>
  );
};
